import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { ref as dbRef, getDatabase, get } from "firebase/database";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { getStorage, ref as storageRef, getDownloadURL } from "firebase/storage";
import STIMULI_HASH_MAP from "./stimuliHashMap";

/**
 * Classify a gaze point against the stimulus bounding boxes and their labels.
 *
 * Returns a color string:
 *   - "black"   → on screen but not on any stimulus
 *   - "#f59e0b" (amber) → on a stimulus but not on a labeled region
 *   - "#ef4444" (red)   → on a sexualized region (SEX_1 or SEX_2)
 *   - "#22c55e" (green) → on the face
 */
function classifyPoint(gx, gy, picBboxes, labelsMap, canvasSize) {
  const px = gx * canvasSize.x;
  const py = gy * canvasSize.y;

  for (const [bboxKey, bbox] of Object.entries(picBboxes)) {
    const [xRange, yRange] = bbox;
    if (px >= xRange[0] && px <= xRange[1] && py >= yRange[0] && py <= yRange[1]) {
      const labels = labelsMap[bboxKey];
      if (labels) {
        // Labels are in the 240x360 coordinate space of the labeling tool.
        const bboxW = xRange[1] - xRange[0];
        const bboxH = yRange[1] - yRange[0];
        const localX = ((px - xRange[0]) / bboxW) * 240;
        const localY = ((py - yRange[0]) / bboxH) * 360;

        if (isInLabel(localX, localY, labels.FACE)) {
          return "#22c55e"; // green — face
        }
        if (isInLabel(localX, localY, labels.SEX_1) || isInLabel(localX, localY, labels.SEX_2)) {
          return "#ef4444"; // red — sexualized
        }
      }
      return "#f59e0b"; // amber — on stimulus, no special region
    }
  }
  return "black";
}

function isInLabel(localX, localY, label) {
  if (!label || !Number.isFinite(label.x1)) return false;
  return localX >= label.x1 && localX <= label.x2 && localY >= label.y1 && localY <= label.y2;
}

/**
 * Convert a bbox key to its uppercase form used in STIMULI_HASH_MAP.
 * "T3_F_05" → "T3_F_05" (already matches)
 * "Ctrl1_04" → "CTRL1_04"
 */
function bboxKeyToMapKey(key) {
  if (key.startsWith("Ctrl")) {
    return key.replace(/^Ctrl/, "CTRL");
  }
  return key;
}

/** Fetch a download URL with sessionStorage cache. */
async function fetchImageUrl(storageHash) {
  const cacheKey = `heatmap_img_${storageHash}`;
  const cached = sessionStorage.getItem(cacheKey);
  if (cached) return cached;

  const storage = getStorage();
  const url = await getDownloadURL(storageRef(storage, `vasi/stimuli/${storageHash}.jpg`));
  sessionStorage.setItem(cacheKey, url);
  return url;
}

/** Fetch labels for a stimulus from Firestore images/{hash} doc. */
async function fetchLabels(storageHash) {
  try {
    const db = getFirestore();
    const snap = await getDoc(doc(db, "images", storageHash));
    if (snap.exists()) {
      return snap.data().labels || {};
    }
    return {};
  } catch {
    return {};
  }
}

export default function GazeHeatmapView({ evalId }) {
  const [trialData, setTrialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  // Cache: bboxKey → download URL
  const [imageUrls, setImageUrls] = useState({});
  // Cache: bboxKey → { FACE, SEX_1, SEX_2 }
  const [labelsCache, setLabelsCache] = useState({});
  // Track which slides have been preloaded
  const preloadedRef = useRef(new Set());

  const containerRef = useRef(null);

  // ── 1. Fetch TrialData from RTDB ──
  useEffect(() => {
    if (!evalId) return;
    setLoading(true);
    get(dbRef(getDatabase(), `evals/${evalId}/TrialData`))
      .then((snap) => {
        if (snap.exists()) {
          setTrialData(snap.val());
        } else {
          setError("No trial data found for this eval.");
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [evalId]);

  // Derived: ordered slide names
  const slideOrder = useMemo(() => {
    if (!trialData?.slide_order) return [];
    return trialData.slide_order.split(",").map((s) => s.trim());
  }, [trialData]);

  const canvasSize = trialData?.canvas_size || { x: 1920, y: 1080 };
  const currentSlideName = slideOrder[currentSlideIndex];

  // ── 2. Preload images + labels for current and adjacent slides ──
  const preloadSlide = useCallback(
    async (slideName) => {
      if (!trialData || !slideName || preloadedRef.current.has(slideName)) return;
      preloadedRef.current.add(slideName);

      const layout = trialData.slide_layouts?.[slideName];
      if (!layout?.pic_bbox) return;

      const bboxKeys = Object.keys(layout.pic_bbox);
      const newUrls = {};
      const newLabels = {};

      await Promise.allSettled(
        bboxKeys.map(async (bboxKey) => {
          const mapKey = bboxKeyToMapKey(bboxKey);
          const storageHash = STIMULI_HASH_MAP[mapKey];
          if (!storageHash) return;

          try {
            const url = await fetchImageUrl(storageHash);
            newUrls[bboxKey] = url;
          } catch (err) {
            console.warn(`[GazeHeatmap] Failed to load image for ${bboxKey}:`, err);
          }

          // Only fetch labels for tanner stimuli (controls have no labels)
          if (!bboxKey.startsWith("Ctrl")) {
            const labels = await fetchLabels(storageHash);
            newLabels[bboxKey] = labels;
          }
        })
      );

      setImageUrls((prev) => ({ ...prev, ...newUrls }));
      setLabelsCache((prev) => ({ ...prev, ...newLabels }));
    },
    [trialData]
  );

  useEffect(() => {
    if (!trialData || slideOrder.length === 0) return;
    const indices = [currentSlideIndex, currentSlideIndex + 1, currentSlideIndex - 1];
    for (const i of indices) {
      if (i >= 0 && i < slideOrder.length) {
        preloadSlide(slideOrder[i]);
      }
    }
  }, [trialData, slideOrder, currentSlideIndex, preloadSlide]);

  // ── 3. Compute classified gaze points for current slide ──
  const gazePoints = useMemo(() => {
    if (!trialData || !currentSlideName) return [];
    const samples = trialData.Trials?.[currentSlideName];
    if (!samples) return [];

    const layout = trialData.slide_layouts?.[currentSlideName];
    const picBboxes = layout?.pic_bbox || {};

    const points = [];
    for (const sample of samples) {
      let gx = null, gy = null;
      const lx = sample.leftEyeX;
      const ly = sample.leftEyeY;
      const rx = sample.rightEyeX;
      const ry = sample.rightEyeY;

      if (lx !== "NaN" && lx != null && Number.isFinite(Number(lx))) {
        gx = Number(lx);
        gy = Number(ly);
      } else if (rx !== "NaN" && rx != null && Number.isFinite(Number(rx))) {
        gx = Number(rx);
        gy = Number(ry);
      }

      if (gx === null || gy === null) continue;
      if (gx < 0 || gx > 1 || gy < 0 || gy > 1) continue;

      const color = classifyPoint(gx, gy, picBboxes, labelsCache, canvasSize);
      points.push({ x: gx, y: gy, color });
    }
    return points;
  }, [trialData, currentSlideName, labelsCache, canvasSize]);

  // ── 4. Canvas rendering ──
  const canvasRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 960, height: 540 });

  useEffect(() => {
    const update = () => {
      if (containerRef.current) {
        const w = containerRef.current.clientWidth;
        const h = w * (canvasSize.y / canvasSize.x);
        setDimensions({ width: w, height: h });
      }
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [canvasSize]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !trialData || !currentSlideName) return;

    const ctx = canvas.getContext("2d");
    const { width, height } = dimensions;
    canvas.width = width;
    canvas.height = height;

    ctx.fillStyle = "#d1d5db";
    ctx.fillRect(0, 0, width, height);

    const scaleX = width / canvasSize.x;
    const scaleY = height / canvasSize.y;

    const layout = trialData.slide_layouts?.[currentSlideName];
    const picBboxes = layout?.pic_bbox || {};

    const imagePromises = Object.entries(picBboxes).map(([bboxKey, bbox]) => {
      const url = imageUrls[bboxKey];
      if (!url) return Promise.resolve();

      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const [xRange, yRange] = bbox;
          const dx = xRange[0] * scaleX;
          const dy = yRange[0] * scaleY;
          const dw = (xRange[1] - xRange[0]) * scaleX;
          const dh = (yRange[1] - yRange[0]) * scaleY;
          ctx.drawImage(img, dx, dy, dw, dh);
          resolve();
        };
        img.onerror = resolve;
        img.src = url;
      });
    });

    Promise.all(imagePromises).then(() => {
      for (const pt of gazePoints) {
        ctx.beginPath();
        ctx.arc(pt.x * width, pt.y * height, 2, 0, Math.PI * 2);
        ctx.fillStyle = pt.color;
        ctx.globalAlpha = 0.6;
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    });
  }, [dimensions, trialData, currentSlideName, imageUrls, gazePoints, canvasSize]);

  // ── Navigation ──
  const goToPrev = () => setCurrentSlideIndex((i) => Math.max(0, i - 1));
  const goToNext = () => setCurrentSlideIndex((i) => Math.min(slideOrder.length - 1, i + 1));

  if (loading) return <p>Loading trial data...</p>;
  if (error) return <p className="text-red-600">{error}</p>;
  if (!trialData) return <p>No trial data available.</p>;

  const validCount = gazePoints.length;
  const totalSamples = trialData.Trials?.[currentSlideName]?.length || 0;

  return (
    <div>
      {/* Navigation */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button
            onClick={goToPrev}
            disabled={currentSlideIndex === 0}
            className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Slide {currentSlideIndex + 1} of {slideOrder.length}
          </span>
          <button
            onClick={goToNext}
            disabled={currentSlideIndex === slideOrder.length - 1}
            className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
        <div className="text-sm text-gray-500">
          <span className="font-medium text-gray-900">{currentSlideName}</span>
          {" — "}
          {validCount} / {totalSamples} valid gaze points
        </div>
      </div>

      {/* Legend */}
      <div className="flex gap-4 mb-3 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-full bg-black" /> Off stimulus
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-full bg-amber-500" /> On stimulus
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-full bg-red-500" /> Sexualized region
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-full bg-green-500" /> Face
        </span>
      </div>

      {/* Canvas */}
      <div ref={containerRef} className="w-full">
        <canvas
          ref={canvasRef}
          className="w-full rounded-lg border border-gray-300"
          style={{ height: dimensions.height }}
        />
      </div>

      {/* Slide selector */}
      <div className="mt-4 flex flex-wrap gap-1">
        {slideOrder.map((name, i) => (
          <button
            key={name}
            onClick={() => setCurrentSlideIndex(i)}
            className={`px-2 py-1 text-xs rounded ${
              i === currentSlideIndex
                ? "bg-indigo-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {name}
          </button>
        ))}
      </div>
    </div>
  );
}
